import { CoordinatesSection } from '@/components/attractions/CoordinatesSection';
import { GenerateAttractionImagesDialog } from '@/components/attractions/GenerateAttractionImagesDialog';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import { useAttractionContext } from '@/pages/attractions/useAttractionContext';
import { Address, Button, EmptyTableState, Loading, RichTextContent } from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, ExternalLink, FileText, ImagePlus, Mail, MapPin, Phone, Sparkles, Volume2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '@startupquickstart/frontend';
import {
  GenerateDescriptionsAction,
  GenerateVoiceoversAction,
} from './actions';

type VoiceoverType = 'both' | 'description' | 'long_description';

export function AttractionOverviewTab() {
  const { attraction, isLoading } = useAttractionContext();
  const queryClient = useQueryClient();
  const [generateDescriptionsModalOpen, setGenerateDescriptionsModalOpen] = useState(false);
  const [generateVoiceoversModalOpen, setGenerateVoiceoversModalOpen] = useState(false);
  const [generateImageModalOpen, setGenerateImageModalOpen] = useState(false);
  const [defaultVoiceoverType, setDefaultVoiceoverType] = useState<VoiceoverType>('both');

  const openVoiceoverDialog = useCallback((type: VoiceoverType) => {
    setDefaultVoiceoverType(type);
    setGenerateVoiceoversModalOpen(true);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!attraction) return null;

  const hasAddress = attraction.streetAddress || attraction.city || attraction.state;

  const hasContactInfo = attraction.phoneNumber || attraction.email || attraction.website;

  return (
    <div className="space-y-6">
      {/* AI Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => setGenerateDescriptionsModalOpen(true)}
        >
          <Sparkles className="h-4 w-4" />
          Generate Descriptions
        </Button>
        <Button
          variant="outline"
          onClick={() => openVoiceoverDialog('both')}
        >
          <Volume2 className="h-4 w-4" />
          Generate Voiceovers
        </Button>
        <Button
          variant="outline"
          onClick={() => setGenerateImageModalOpen(true)}
        >
          <ImagePlus className="h-4 w-4" />
          Generate Image
        </Button>
      </div>

      {/* Two-column content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-6">
          {/* Description card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Description</h2>
              <p className="text-sm text-muted-foreground">
                Short description used in listings and previews
              </p>
            </div>
            <div className="p-6 space-y-6">
              {/* Voiceover above description */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {attraction.voiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={attraction.voiceover}
                    isInSync={attraction.isDescriptionVoiceoverInSync ?? null}
                    outOfSyncMessage="Description has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">No voiceover generated</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openVoiceoverDialog('description')}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              {/* Description text */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {attraction.description ? (
                  <RichTextContent content={attraction.description} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No description"
                    description="Edit to add a description"
                  />
                )}
              </div>
            </div>
            <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Created: {formatDate(attraction.createdAt)}</span>
              <span>Updated: {formatDate(attraction.updatedAt)}</span>
            </div>
          </div>

          {/* Long Description card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Long Description
              </h2>
              <p className="text-sm text-muted-foreground">
                Detailed description used in the full attraction view
              </p>
            </div>
            <div className="p-6 space-y-6">
              {/* Voiceover above long description */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {attraction.longVoiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={attraction.longVoiceover}
                    isInSync={attraction.isLongDescriptionVoiceoverInSync ?? null}
                    outOfSyncMessage="Long description has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">No voiceover generated</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openVoiceoverDialog('long_description')}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              {/* Long description text */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {attraction.longDescription ? (
                  <RichTextContent content={attraction.longDescription} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No long description"
                    description="Edit to add a long description"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Location & Address card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Location & Address
              </h2>
              <p className="text-sm text-muted-foreground">Where this attraction is located</p>
            </div>

            {/* Interactive coordinates editor: warning when missing,
                Verified badge when confirmed, Use Google Places to geocode
                from the saved (or sparse) address fields, drag/click to
                reposition, and an expand button for a fullscreen view. */}
            <div className="px-6 pt-6">
              <CoordinatesSection attraction={attraction} />
            </div>

            <div className="p-6">
              <div className="divide-y">
                {/* Greater Area */}
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    Greater Area
                  </div>
                  <div className="text-sm">
                    {attraction.greaterArea ? (
                      <Link
                        className="text-primary hover:underline"
                        to={`/greater-areas/${attraction.greaterArea.id}`}
                      >
                        {attraction.greaterArea.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">Not set</span>
                    )}
                  </div>
                </div>

                {/* Neighborhood */}
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    Neighborhood
                  </div>
                  <div className="text-sm">
                    {attraction.neighborhood ? (
                      <Link
                        className="text-primary hover:underline"
                        to={`/neighborhoods/${attraction.neighborhood.id}`}
                      >
                        {attraction.neighborhood.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">Not set</span>
                    )}
                  </div>
                </div>

                {/* Address */}
                {hasAddress && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      Address
                    </div>
                    <div className="text-sm">
                      <Address
                        streetAddress={attraction.streetAddress}
                        streetAddress2={attraction.streetAddress2}
                        city={attraction.city}
                        state={attraction.state}
                        postalCode={attraction.postalCode}
                        country={attraction.country}
                      />
                    </div>
                  </div>
                )}

                {/* Coordinates are now rendered + edited inside the
                    CoordinatesSection above the divided list. */}
              </div>
            </div>

          </div>

          {/* Contact Information card */}
          {hasContactInfo && (
            <div className="rounded-xl border bg-card shadow-sm">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-medium tracking-tight text-zinc-900">Contact</h2>
                <p className="text-sm text-muted-foreground">Contact information</p>
              </div>
              <div className="p-6">
                <div className="divide-y">
                  {attraction.phoneNumber && (
                    <div className="py-3 first:pt-0">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        Phone
                      </div>
                      <div className="text-sm">{attraction.phoneNumber}</div>
                    </div>
                  )}
                  {attraction.email && (
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        Email
                      </div>
                      <div className="text-sm">
                        <a
                          href={`mailto:${attraction.email}`}
                          className="text-primary hover:underline"
                        >
                          {attraction.email}
                        </a>
                      </div>
                    </div>
                  )}
                  {attraction.website && (
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                        Website
                      </div>
                      <div className="text-sm">
                        <a
                          href={
                            attraction.website.startsWith('http')
                              ? attraction.website
                              : `https://${attraction.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {attraction.website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate Descriptions Modal */}
      <GenerateDescriptionsAction
        attractionId={attraction.id}
        open={generateDescriptionsModalOpen}
        onOpenChange={setGenerateDescriptionsModalOpen}
      />

      {/* Generate Voiceovers Modal */}
      <GenerateVoiceoversAction
        attractionId={attraction.id}
        open={generateVoiceoversModalOpen}
        onOpenChange={setGenerateVoiceoversModalOpen}
        defaultVoiceoverType={defaultVoiceoverType}
      />

      {/* Generate Image Modal (reference-photo confirmation + options).
          setAsFeatured defaults true inside the dialog — the featured image
          is the point of generating from the overview. Refresh the detail
          query on kickoff so the hero image updates once the job lands. */}
      <GenerateAttractionImagesDialog
        attractionId={attraction.id}
        open={generateImageModalOpen}
        onOpenChange={setGenerateImageModalOpen}
        onGenerated={() => {
          queryClient.invalidateQueries({ queryKey: ['Attraction', attraction.id] });
        }}
      />
    </div>
  );
}
